// islandWorld.js
import * as THREE from 'three';
import { IslandWorldChunk } from './islandWorldChunk';
import { db, functions } from '@/config/firebase';
import { blocks } from './blocks';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, collection, onSnapshot } from 'firebase/firestore';

export class IslandWorld extends THREE.Group {
  statusTimeoutId = null;
  modCache = {};
  pendingSaves = new Map();
  debounceTimeout = null;
  unsubscribeSnapshot = null;
  addBlockToWorldCallable = httpsCallable(functions, 'addBlockToWorld');
  destroyBlockCallable = httpsCallable(functions, "destroyBlock");

  constructor(islandData) {
    super();
    this.islandData = islandData;
    this.chunk = null;
    this.disposed = false;
    this.eventListeners = new Map();
    this.startRealtimeModificationSync();
  }

  normalizeCoord(value) {
    return Math.round(Number(value));
  }

  normalizeCoords(x, y, z) {
    return {
      x: this.normalizeCoord(x),
      y: this.normalizeCoord(y),
      z: this.normalizeCoord(z),
    };
  }

  getWorldCoordKey(x, y, z) {
    return `${this.normalizeCoord(x)}_${this.normalizeCoord(y)}_${this.normalizeCoord(z)}`;
  }

  emit(eventName, data) {
    const listeners = this.eventListeners.get(eventName);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  on(eventName, callback) {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, []);
    }
    this.eventListeners.get(eventName).push(callback);
  }

  async generate() {
    if (this.disposed) return;
    const { coords, id } = this.islandData;

    // Validate coords exist (islands are floating structures with specific coordinates)
    if (!coords || typeof coords.x !== 'number') {
      throw new Error(`Island ${id} has missing or invalid coords. Cannot generate island.`);
    }

    this.chunk = new IslandWorldChunk(coords, id);
    this.chunk.position.set(coords.x, coords.y, coords.z);
    this.add(this.chunk);

    await this.chunk.generate();
    if (this.disposed) return;
    await this.loadPlayerChanges()

    if (this.disposed) return;
    this.chunk.generateMeshes();
    this.chunk.loaded = true;
  }

  async loadPlayerChanges() {
    if (this.disposed) return;
    try {
      const ref = doc(db, 'islandMods', this.islandData.id);
      const snapshot = await getDoc(ref);

      if (snapshot.exists()) {
        const { blocks } = snapshot.data();
        if (blocks && typeof blocks === 'object') {
          for (const coord in blocks) {
            const [x, y, z] = coord.split('_').map(Number);
            if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
              const cx = x - this.chunk.position.x;
              const cy = y - this.chunk.position.y;
              const cz = z - this.chunk.position.z;
              this.chunk.setBlockId(cx, cy, cz, blocks[coord]);
            } else {
              console.warn(`[IslandWorldChunk] Invalid coordinate in Firestore: ${coord}`);
            }
          }
        } else {
          console.warn(`[IslandWorldChunk] No valid block data for chunk ${chunkKey}`);
        }
      }
    } catch (error) {
      console.error(`[IslandWorldChunk] Failed to load player changes for chunk ${this.position.x}_${this.position.z}:`, error);
    }
  }

  getBlock(x, y, z) {
    if (!this.chunk) return { id: 0 };

    const nx = this.normalizeCoord(x);
    const ny = this.normalizeCoord(y);
    const nz = this.normalizeCoord(z);
    const worldCoordKey = this.getWorldCoordKey(nx, ny, nz);
    const cx = Math.floor(nx - this.chunk.position.x);
    const cy = Math.floor(ny - this.chunk.position.y);
    const cz = Math.floor(nz - this.chunk.position.z);

    const cachedId = this.modCache[worldCoordKey];
    if (cachedId !== undefined) {
      return { id: cachedId };
    }

    // Use chunk.getBlock() instead of accessing deprecated data array
    return this.chunk.getBlock(cx, cy, cz);
  }


  async addBlock(x, y, z, blockId, userId) {
    if (!this.chunk) return;
    const normalized = this.normalizeCoords(x, y, z);
    const xInt = normalized.x;
    const yInt = normalized.y;
    const zInt = normalized.z;

    const cx = Math.floor(xInt - this.chunk.position.x);
    const cy = Math.floor(yInt - this.chunk.position.y);
    const cz = Math.floor(zInt - this.chunk.position.z);
    const worldCoordKey = this.getWorldCoordKey(xInt, yInt, zInt);

    // Optimistic Update
    const oldBlockId = this.chunk.getBlock(cx, cy, cz).id;
    const previousModOverride = this.modCache[worldCoordKey];
    this.modCache[worldCoordKey] = blockId;
    this.chunk.setBlockId(cx, cy, cz, blockId);
    this.chunk.addBlockInstance(cx, cy, cz);
    this.revealAdjacentBlocks(xInt, yInt, zInt);

    try {
      const result = await this.addBlockToWorldCallable({
        blockId,
        locationType: "island",
        x: xInt,
        y: yInt,
        z: zInt,
        islandId: this.islandData.id
      });

      const responseData = result.data;

      if (!responseData.success) {
        if (oldBlockId === blocks.empty.id) {
          this.chunk.deleteBlockInstance(cx, cy, cz);
          this.chunk.setBlockId(cx, cy, cz, oldBlockId);
        }
        if (previousModOverride === undefined) {
          delete this.modCache[worldCoordKey];
        } else {
          this.modCache[worldCoordKey] = previousModOverride;
        }
        this.revealAdjacentBlocks(xInt, yInt, zInt);
        console.error("addBlockToWorldCallable returned false unexpectedly:", responseData?.message);
        this._notify(responseData?.message || "BlockType not in inventory!");
      }
    } catch (error) {
      if (oldBlockId === blocks.empty.id) {
        this.chunk.deleteBlockInstance(cx, cy, cz);
        this.chunk.setBlockId(cx, cy, cz, oldBlockId);
      }
      if (previousModOverride === undefined) {
        delete this.modCache[worldCoordKey];
      } else {
        this.modCache[worldCoordKey] = previousModOverride;
      }
      this.revealAdjacentBlocks(xInt, yInt, zInt);
      console.error(`Failed to call addBlockToWorldCallable or inventory check failed:`, error);
      this._notify(error.message || "Adding block failed", 'error');
    }
  }

  async removeBlock(x, y, z, userId) {
    if (!this.chunk) return;
    const originalX = Number(x);
    const originalY = Number(y);
    const originalZ = Number(z);
    const normalized = this.normalizeCoords(x, y, z);
    const xInt = normalized.x;
    const yInt = normalized.y;
    const zInt = normalized.z;

    const cx = Math.floor(xInt - this.chunk.position.x);
    const cy = Math.floor(yInt - this.chunk.position.y);
    const cz = Math.floor(zInt - this.chunk.position.z);
    const worldCoordKey = this.getWorldCoordKey(xInt, yInt, zInt);

    // Optimistic Update
    const oldBlockId = this.chunk.getBlock(cx, cy, cz).id;
    const previousModOverride = this.modCache[worldCoordKey];
    this.modCache[worldCoordKey] = blocks.empty.id;
    this.chunk.deleteBlockInstance(cx, cy, cz);
    this.chunk.setBlockId(cx, cy, cz, blocks.empty.id);
    this.revealAdjacentBlocks(xInt, yInt, zInt);
    try {
      const result = await this.destroyBlockCallable({
        locationType: "island",
        x: xInt,
        y: yInt,
        z: zInt,
        islandId: this.islandData.id,
      });
      const responseData = result.data;

      if (!responseData.success) {
        this.chunk.setBlockId(cx, cy, cz, oldBlockId);
        this.chunk.addBlockInstance(cx, cy, cz);
        this.revealAdjacentBlocks(xInt, yInt, zInt);

        const errorMessage = responseData?.message || "Cannot remove block.";
        this._notify(errorMessage);
        console.error("Server reported failure:", responseData);
      }
    } catch (error) {
      if (
        error?.code === "not-found" &&
        (originalX !== xInt || originalY !== yInt || originalZ !== zInt)
      ) {
        try {
          const retryResult = await this.destroyBlockCallable({
            locationType: "island",
            x: originalX,
            y: originalY,
            z: originalZ,
            islandId: this.islandData.id,
          });
          const retryData = retryResult.data;
          if (retryData?.success) {
            this.modCache[worldCoordKey] = blocks.empty.id;
            this.revealAdjacentBlocks(xInt, yInt, zInt);
            return;
          }
        } catch (_) {
          // Fall through to optimistic rollback below
        }
      }

      this.chunk.setBlockId(cx, cy, cz, oldBlockId);
      this.chunk.addBlockInstance(cx, cy, cz);
      if (previousModOverride === undefined) {
        delete this.modCache[worldCoordKey];
      } else {
        this.modCache[worldCoordKey] = previousModOverride;
      }
      this.revealAdjacentBlocks(xInt, yInt, zInt);
      console.error("Error calling destroyBlockCallable:", error);
      this._notify(error.message || "Failed to remove block", 'error');
    }
  }

  revealAdjacentBlocks(x, y, z) {
    const offsets = [
      [-1, 0, 0], [1, 0, 0],
      [0, -1, 0], [0, 1, 0],
      [0, 0, -1], [0, 0, 1],
    ];

    for (const [dx, dy, dz] of offsets) {
      const nx = x + dx;
      const ny = y + dy;
      const nz = z + dz;
      this.revealBlock(nx, ny, nz);
    }
  }

  revealBlock(x, y, z) {
    if (!this.chunk?.loaded) return;

    const cx = Math.floor(x - this.chunk.position.x);
    const cy = Math.floor(y - this.chunk.position.y);
    const cz = Math.floor(z - this.chunk.position.z);

    if (this.chunk.inBounds(cx, cy, cz)) {
      if (!this.chunk.isBlockObscured(cx, cy, cz)) {
        this.chunk.addBlockInstance(cx, cy, cz);
      }
    }
  }

  hideBlock(x, y, z) {
    if (!this.chunk?.loaded) return;

    const cx = Math.floor(x - this.chunk.position.x);
    const cy = Math.floor(y - this.chunk.position.y);
    const cz = Math.floor(z - this.chunk.position.z);

    if (this.chunk.inBounds(cx, cy, cz)) {
      if (this.chunk.isBlockObscured(cx, cy, cz)) {
        this.chunk.deleteBlockInstance(cx, cy, cz);
      }
    }
  }

  _notify(text, type = 'info') {
    this.emit('notify', { text, type });

    const status = document.getElementById('status');
    if (status) {
      if (this.statusTimeoutId) {
        clearTimeout(this.statusTimeoutId);
      }
      status.innerText = text;
      status.classList.add('show');
      this.statusTimeoutId = setTimeout(() => {
        status.classList.remove('show');
        setTimeout(() => {
          status.innerText = '';
          this.statusTimeoutId = null;
        }, 400);
      }, 3000);
    }
  }


  startRealtimeModificationSync() {
    const islandKey = this.islandData.id;
    const ref = doc(db, 'islandMods', islandKey);

    this.unsubscribeSnapshot = onSnapshot(ref, (docSnapshot) => {
      if (!this.chunk?.loaded) return;

      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const incomingMods = data.blocks || {};

        // Merge with pending saves if needed
        const mergedMods = { ...incomingMods, ...(this.pendingSaves.get(islandKey) || {}) };
        this.modCache = mergedMods;

        // Process each modified block
        for (const coord in incomingMods) {
          const [x, y, z] = coord.split('_').map(c => Math.floor(Number(c)));

          const cx = x - this.chunk.position.x;
          const cy = y - this.chunk.position.y;
          const cz = z - this.chunk.position.z;

          if (!this.chunk.inBounds(cx, cy, cz)) continue;

          const blockId = incomingMods[coord];
          const currentBlock = this.chunk.getBlock(cx, cy, cz);

          // Skip if no actual change
          if (currentBlock.id === blockId) continue;

          // Use proper block methods
          if (blockId === blocks.empty.id) {
            this.chunk.deleteBlockInstance(cx, cy, cz);
            this.chunk.setBlockId(cx, cy, cz, 0);
          } else {
            this.chunk.setBlockId(cx, cy, cz, blockId);
            this.chunk.addBlockInstance(cx, cy, cz);
          }

          this.revealAdjacentBlocks(x, y, z);
        }
      }
    }, (error) => {
      console.error("[IslandWorld] Error fetching island modifications:", error);
    });
  }

  dispose() {
    this.disposed = true;
    if (this.unsubscribeSnapshot) {
      this.unsubscribeSnapshot();
      this.unsubscribeSnapshot = null;
    }

    this.traverse(obj => {
      if (typeof obj.dispose === 'function') {
        obj.dispose();
      }
    });
    this.clear();
  }
}
